import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AudiencePicker } from "@/components/AudiencePicker";
import {
    FlowArrow, PlusCircle, ArrowLeft, X, CircleNotch, Trash, Play, Pause,
    Users, ClockCountdown, PaperPlaneTilt, Sparkle, PencilSimple, CopySimple,
    Target, ChatCircleDots, ListBullets, ArrowRight, Clock, CheckCircle, Flag, TestTube,
} from "@phosphor-icons/react";

const FIELD_OPTIONS = [
    { value: "name", label: "Contact name" },
    { value: "company", label: "Company" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
];
const STATUS_BADGE = {
    draft: "bg-stone-100 text-stone-600",
    active: "bg-emerald-100 text-emerald-700",
    paused: "bg-amber-100 text-amber-700",
};
const RUN_BADGE = {
    active: "bg-sky-100 text-sky-700", sending: "bg-indigo-100 text-indigo-700",
    completed: "bg-emerald-100 text-emerald-700", stopped: "bg-amber-100 text-amber-700",
    failed: "bg-red-100 text-red-700",
};

const ACTION_BASE = [["next", "Continue to next step"], ["exit", "End journey"]];
const STEP_KINDS = [["template", "Template message"], ["freeform", "Free-form message"], ["condition", "Condition (Salesforce)"]];

function ActionSelect({ value, onChange, stepCount, selfIdx, tid }) {
    return (
        <select data-testid={tid} value={value || "next"} onChange={(e) => onChange(e.target.value)}
            className="rounded-lg border border-stone-200 px-2 py-1.5 text-sm">
            {ACTION_BASE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            {Array.from({ length: stepCount }).map((_, k) => (k !== selfIdx ? <option key={k} value={`goto:${k}`}>{`Go to step ${k + 1}`}</option> : null))}
        </select>
    );
}

// Merge a step's touched button actions with ALL its template quick-reply buttons
// (so every option has a destination) + non-blank free-text rules. Shared by save + harness.
function mergeBranches(s, templates) {
    if (!(s.wait_for_reply && s.reply_mode === "match")) return [];
    const tplBtns = ((templates || []).find((t) => t.name === s.template_name)?.buttons || []).filter((b) => b.type === "QUICK_REPLY");
    const byVal = Object.fromEntries((s.reply_branches || []).map((b) => [b.value, b]));
    const btn = tplBtns.map((b) => ({ match_type: "button", value: b.text, action: byVal[b.text]?.action || "next" }));
    const text = (s.reply_branches || []).filter((b) => b.match_type !== "button" && (b.value || "").trim());
    return btn.concat(text);
}

function fmtBranchAction(a, steps) {
    if (a === "exit") return "Exit journey";
    if (!a || a === "next") return "Continue to next step";
    if (a.startsWith && a.startsWith("goto:")) {
        const idx = Number(a.split(":")[1]);
        const nm = steps?.[idx]?.template_name || steps?.[idx]?.body_text || `Step ${idx + 1}`;
        return `Go to Step ${idx + 1} (${(nm || "").slice(0, 24)})`;
    }
    return a;
}

// Test harness: simulate a customer response and preview which branch fires (backend matcher).
function BranchHarness({ step, steps, branches, sidx }) {
    const [kind, setKind] = useState("button");
    const [val, setVal] = useState("");
    const [res, setRes] = useState(null);
    const [busy, setBusy] = useState(false);
    const tplBtns = branches.filter((b) => b.match_type === "button");
    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/whatsapp/journeys/preview-branch", {
                reply_mode: step.reply_mode || "any", reply_branches: branches,
                on_other_reply: step.on_other_reply || "next", on_reply: step.on_reply || "exit",
                on_no_reply: step.on_no_reply || "next",
                response_kind: kind, response_value: kind === "button" ? (val || tplBtns[0]?.value || "") : val,
            });
            setRes(data);
        } catch { toast.error("Preview failed"); } finally { setBusy(false); }
    };
    return (
        <div className="mt-3 rounded-xl border border-dashed border-indigo-200 bg-indigo-50/40 p-3" data-testid={`journey-harness-${sidx}`}>
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-indigo-600"><TestTube size={13} weight="duotone" /> Test a reply</div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <select data-testid={`journey-harness-kind-${sidx}`} value={kind} onChange={(e) => { setKind(e.target.value); setRes(null); }} className="rounded-lg border border-stone-200 px-2 py-1">
                    <option value="button">Button</option><option value="text">Text</option><option value="timeout">No reply (timeout)</option>
                </select>
                {kind === "button" && (
                    <select data-testid={`journey-harness-value-${sidx}`} value={val} onChange={(e) => setVal(e.target.value)} className="min-w-[150px] rounded-lg border border-stone-200 px-2 py-1">
                        <option value="">{tplBtns.length ? "Pick a button" : "No buttons"}</option>
                        {tplBtns.map((b) => <option key={b.value} value={b.value}>{b.value}</option>)}
                    </select>
                )}
                {kind === "text" && <input data-testid={`journey-harness-value-${sidx}`} value={val} onChange={(e) => setVal(e.target.value)} placeholder="type a reply…" className="w-40 rounded-lg border border-stone-200 px-2 py-1" />}
                <button type="button" data-testid={`journey-harness-run-${sidx}`} onClick={run} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-3 py-1 font-semibold text-white disabled:opacity-50">Preview</button>
                {res && (
                    <span data-testid={`journey-harness-result-${sidx}`} className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 font-semibold text-indigo-700 shadow-sm">
                        <ArrowRight size={12} /> {res.branch === "no_reply" ? "No reply" : res.branch === "any_other" ? "Any other reply" : res.branch === "any_reply" ? "Any reply" : `“${res.branch}”`} → {fmtBranchAction(res.action, steps)}
                    </span>
                )}
            </div>
        </div>
    );
}

const emptyStep = () => ({ kind: "template", template_name: "", template_language: "en_US", body_params: [], body_text: "", cond_field: "", cond_equals: "", delay_value: 0, delay_unit: "hours", wait_for_reply: false, reply_window_hours: 48, reply_mode: "any", reply_branches: [], on_other_reply: "next", on_reply: "exit", on_no_reply: "next", on_match: "next", on_else: "next" });
const toHours = (s) => (s.delay_unit === "days" ? Number(s.delay_value || 0) * 24 : Number(s.delay_value || 0));
const humanizeTpl = (name) => String(name || "").replace(/^iw[_-]/i, "").replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();
const fromHours = (h) => (h && h % 24 === 0 ? { delay_value: h / 24, delay_unit: "days" } : { delay_value: h || 0, delay_unit: "hours" });

/* Hours before a step's message once a contact REACHES it. Handles both builder state
   (delay_value/delay_unit) and raw journey docs (delay_hours). */
const stepHours = (s) => (s && s.delay_hours != null ? Number(s.delay_hours) : toHours(s));
/* Human label for a step's own entry delay. */
const fmtDelay = (h) => h <= 0 ? "Instant" : (h % 24 === 0 ? `+${h / 24}d` : (h < 24 ? `+${h}h` : `+${Math.floor(h / 24)}d ${h % 24}h`));
/* Human label for a reply-wait window (hours). */
const fmtWin = (h) => { h = Number(h) || 0; return h <= 0 ? "" : (h % 24 === 0 ? `${h / 24}d` : (h < 24 ? `${h}h` : `${Math.floor(h / 24)}d ${h % 24}h`)); };

/* Shared enterprise flow strip — used in both the builder (edit) and the read-only detail view. */
function JourneyFlowStrip({ steps, goal }) {
    const list = steps || [];
    const g = goal || {};
    const kindIcon = (k) => k === "condition" ? <Flag size={13} weight="duotone" /> : (k === "freeform" ? <ChatCircleDots size={13} weight="duotone" /> : <PaperPlaneTilt size={13} weight="duotone" />);
    return (
        <div data-testid="journey-timeline" className="mt-5 rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
                <FlowArrow size={15} weight="duotone" className="text-emerald-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">Journey flow</span>
                <span className="ml-auto text-[11px] font-medium text-stone-400">{list.length} step{list.length === 1 ? "" : "s"}</span>
            </div>
            <div className="overflow-x-auto pb-1">
                <div className="flex min-w-max items-stretch gap-0">
                    <div className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-gradient-to-b from-emerald-500 to-emerald-600 px-4 py-3 text-center text-white shadow-sm shadow-emerald-600/20">
                        <Play size={15} weight="fill" /><span className="text-[9px] font-bold uppercase tracking-wider">Enter</span>
                    </div>
                    {list.map((s, i) => {
                        const d = stepHours(s);
                        return (
                            <div key={i} className="flex items-center">
                                <div className="h-px w-6 shrink-0 bg-stone-200" />
                                <div className="group min-w-[136px] rounded-2xl border border-stone-200 bg-white px-3.5 py-2.5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md">
                                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-stone-700">
                                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-stone-900 text-[9px] font-extrabold text-white">{i + 1}</span>
                                        <span className="text-emerald-600">{kindIcon(s.kind)}</span>
                                        <span className="max-w-[92px] truncate">{s.kind === "condition" ? "Condition" : (s.kind === "freeform" ? "Message" : (humanizeTpl(s.template_name) || "Template"))}</span>
                                    </div>
                                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${d <= 0 ? "bg-stone-100 text-stone-500" : "bg-amber-50 text-amber-700"}`}>
                                            <Clock size={10} weight="fill" /> {fmtDelay(d)}
                                        </span>
                                        {s.wait_for_reply && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                                <ChatCircleDots size={10} weight="fill" /> reply{fmtWin(s.reply_window_hours) ? ` ${fmtWin(s.reply_window_hours)}` : ""}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div className="h-px w-6 shrink-0 self-center bg-stone-200" />
                    <div className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-gradient-to-b from-stone-800 to-stone-900 px-4 py-3 text-center text-white shadow-sm">
                        {g.enabled ? <Target size={15} weight="fill" /> : <CheckCircle size={15} weight="fill" />}
                        <span className="text-[9px] font-bold uppercase tracking-wider">{g.enabled ? "Goal" : "End"}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}


/* ---------------- Builder ---------------- */
function JourneyBuilder({ existing, prefillSteps, onCancel, onSaved }) {
    const [name, setName] = useState(existing?.name || "");
    const [templates, setTemplates] = useState(null);
    const [steps, setSteps] = useState(() => {
        const src = existing?.steps?.length ? existing.steps : (prefillSteps?.length ? prefillSteps : null);
        return src
            ? src.map((s) => ({
                kind: s.kind || "template",
                template_name: s.template_name || "", template_language: s.template_language || "en_US",
                body_params: s.body_params || [], body_text: s.body_text || "",
                cond_field: s.cond_field || "", cond_equals: s.cond_equals || "",
                wait_for_reply: !!s.wait_for_reply, reply_window_hours: s.reply_window_hours ?? 48,
                reply_mode: s.reply_mode || "any", reply_branches: s.reply_branches || [], on_other_reply: s.on_other_reply || "next",
                on_reply: s.on_reply || "exit", on_no_reply: s.on_no_reply || "next",
                on_match: s.on_match || "next", on_else: s.on_else || "next",
                ...fromHours(s.delay_hours || 0),
            }))
            : [emptyStep()];
    });
    const [kw, setKw] = useState(existing?.keyword_trigger || { enabled: false, keyword: "" });
    const [auto, setAuto] = useState(() => {
        const ds = existing?.auto_enroll?.data_source || {};
        return {
            enabled: existing?.auto_enroll?.enabled || false,
            kind: ds.kind && ds.kind !== "csv" ? ds.kind : "sf_leads",
            list_view_object: ds.list_view_object || "Lead", list_view_id: ds.list_view_id || "",
            report_id: ds.report_id || "", sf_campaign_id: ds.sf_campaign_id || "",
        };
    });
    // Entry-trigger mode picker: how contacts ENTER this journey.
    const _ds0 = existing?.auto_enroll?.data_source || {};
    const _hasFilters = !!(_ds0.filters && Object.values(_ds0.filters).some((v) => v));
    const [triggerMode, setTriggerMode] = useState(() =>
        existing ? (existing.auto_enroll?.enabled ? "sf" : (existing.keyword_trigger?.enabled ? "keyword" : "manual")) : "manual");
    const [autoMode, setAutoMode] = useState(() =>
        ((_ds0.kind === "sf_leads" || _ds0.kind === "sf_contacts") && _hasFilters) ? "stage"
            : (["sf_listview", "sf_report", "sf_campaign", "sf_leads", "sf_contacts"].includes(_ds0.kind) && !_hasFilters ? "list" : "stage"));
    const [stage, setStage] = useState(() => {
        const field = _hasFilters ? (["status", "rating", "lead_source", "industry", "state"].find((k) => _ds0.filters?.[k]) || "status") : "status";
        return { object: _ds0.kind === "sf_contacts" ? "Contact" : "Lead", field, value: _hasFilters ? (_ds0.filters?.[field] || "") : "" };
    });
    const [filterOpts, setFilterOpts] = useState({});
    const [lastSynced, setLastSynced] = useState(existing?.auto_enroll?.last_synced || null);
    const [syncing, setSyncing] = useState(false);
    const [sfOpts, setSfOpts] = useState({ listviews: [], reports: [], campaigns: [] });
    const [sw, setSw] = useState(existing?.send_window || { respect_business_hours: false });
    const [goal, setGoal] = useState(existing?.goal || { enabled: false, field: "", equals: "" });
    const [goalFields, setGoalFields] = useState([]);
    const [saving, setSaving] = useState(false);

    // Load Lead fields for the goal + condition pickers (checked against the enrolled SF record).
    useEffect(() => {
        api.get("/integrations/salesforce/describe/Lead?all=1")
            .then((r) => setGoalFields(r.data.fields || [])).catch(() => setGoalFields([]));
    }, []);

    // List/report/campaign options (list mode).
    useEffect(() => {
        if (triggerMode !== "sf" || autoMode !== "list") return;
        if (auto.kind === "sf_listview") api.get(`/whatsapp/broadcasts/sf-listviews?sobject=${auto.list_view_object}`).then((r) => setSfOpts((o) => ({ ...o, listviews: r.data.listviews || [] }))).catch(() => {});
        if (auto.kind === "sf_report") api.get("/whatsapp/broadcasts/sf-reports").then((r) => setSfOpts((o) => ({ ...o, reports: r.data.reports || [] }))).catch(() => {});
        if (auto.kind === "sf_campaign") api.get("/whatsapp/broadcasts/sf-campaigns").then((r) => setSfOpts((o) => ({ ...o, campaigns: r.data.campaigns || [] }))).catch(() => {});
    }, [triggerMode, autoMode, auto.kind, auto.list_view_object]);

    // Picklist values for the stage/field builder.
    useEffect(() => {
        if (triggerMode !== "sf" || autoMode !== "stage") return;
        const kind = stage.object === "Contact" ? "sf_contacts" : "sf_leads";
        api.get(`/whatsapp/broadcasts/sf-filter-options?kind=${kind}`).then((r) => setFilterOpts(r.data || {})).catch(() => setFilterOpts({}));
    }, [triggerMode, autoMode, stage.object]);

    const STAGE_FIELDS = [
        ["status", "Lead Status (stage)"], ["rating", "Rating"], ["lead_source", "Lead Source"],
        ["industry", "Industry"], ["state", "State"],
    ];

    const buildDS = () => {
        if (triggerMode !== "sf") return { kind: "csv" };
        if (autoMode === "stage") {
            return { kind: stage.object === "Contact" ? "sf_contacts" : "sf_leads",
                     filters: { [stage.field]: (stage.value || "").trim() } };
        }
        const d = { kind: auto.kind };
        if (auto.kind === "sf_listview") { d.list_view_object = auto.list_view_object; d.list_view_id = auto.list_view_id; }
        if (auto.kind === "sf_report") d.report_id = auto.report_id;
        if (auto.kind === "sf_campaign") d.sf_campaign_id = auto.sf_campaign_id;
        return d;
    };

    const syncNow = async () => {
        if (!existing?.id) return;
        setSyncing(true);
        try {
            const { data } = await api.post(`/whatsapp/journeys/${existing.id}/sync`);
            setLastSynced(data.last_synced || new Date().toISOString());
            toast.success(data.new_count ? `Enrolled ${data.new_count} new contact(s)` : "Synced — no new matches yet");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Sync failed"); }
        finally { setSyncing(false); }
    };

    useEffect(() => {
        api.get("/whatsapp/broadcasts/templates")
            .then((r) => setTemplates((r.data.templates || []).filter((t) => t.usable)))
            .catch(() => setTemplates([]));
    }, []);

    const setStep = (i, patch) => setSteps((xs) => xs.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
    const onTemplate = (i, nm) => {
        const t = (templates || []).find((x) => x.name === nm);
        const n = t?.variable_count || 0;
        setStep(i, { template_name: nm, template_language: t?.language || "en_US", body_params: Array.from({ length: n }, () => ({ source: "static", value: "" })) });
    };
    const setParam = (i, pi, patch) => setSteps((xs) => xs.map((s, idx) =>
        idx === i ? { ...s, body_params: s.body_params.map((p, j) => (j === pi ? { ...p, ...patch } : p)) } : s));

    // Match-response branch helpers (button branches keyed by label; free-text rules are separate).
    const btnAction = (s, value) => (s.reply_branches || []).find((b) => b.match_type === "button" && b.value === value)?.action || "next";
    const upsertBtnBranch = (i, value, action) => setSteps((xs) => xs.map((s, idx) => {
        if (idx !== i) return s;
        const others = (s.reply_branches || []).filter((b) => !(b.match_type === "button" && b.value === value));
        return { ...s, reply_branches: [...others, { match_type: "button", value, action }] };
    }));
    const addTextRule = (i) => setSteps((xs) => xs.map((s, idx) =>
        idx === i ? { ...s, reply_branches: [...(s.reply_branches || []), { match_type: "contains", value: "", action: "next" }] } : s));
    const setTextRule = (i, ri, patch) => setSteps((xs) => xs.map((s, idx) => {
        if (idx !== i) return s;
        const btns = (s.reply_branches || []).filter((b) => b.match_type === "button");
        const rules = (s.reply_branches || []).filter((b) => b.match_type !== "button").map((b, j) => (j === ri ? { ...b, ...patch } : b));
        return { ...s, reply_branches: [...btns, ...rules] };
    }));
    const removeTextRule = (i, ri) => setSteps((xs) => xs.map((s, idx) => {
        if (idx !== i) return s;
        const btns = (s.reply_branches || []).filter((b) => b.match_type === "button");
        const rules = (s.reply_branches || []).filter((b) => b.match_type !== "button").filter((_, j) => j !== ri);
        return { ...s, reply_branches: [...btns, ...rules] };
    }));

    const save = async () => {
        if (!name.trim()) return toast.error("Name your journey.");
        for (let i = 0; i < steps.length; i++) {
            const s = steps[i];
            const kind = s.kind || "template";
            if (kind === "template" && !s.template_name) return toast.error(`Step ${i + 1}: pick a template.`);
            if (kind === "freeform" && !(s.body_text || "").trim()) return toast.error(`Step ${i + 1}: write the free-form message.`);
            if (kind === "condition" && !(s.cond_field || "").trim()) return toast.error(`Step ${i + 1}: choose a Salesforce field.`);
        }
        if (triggerMode === "sf" && autoMode === "stage" && !(stage.value || "").trim())
            return toast.error("Pick the value that should trigger enrolment (e.g. Status = Qualified).");
        if (triggerMode === "sf" && autoMode === "list") {
            if (auto.kind === "sf_listview" && !auto.list_view_id) return toast.error("Choose a Salesforce list view.");
            if (auto.kind === "sf_report" && !auto.report_id) return toast.error("Choose a Salesforce report.");
            if (auto.kind === "sf_campaign" && !auto.sf_campaign_id) return toast.error("Choose a Salesforce campaign.");
        }
        if (triggerMode === "keyword" && !(kw.keyword || "").trim())
            return toast.error("Enter the keyword that enrols a contact.");
        setSaving(true);
        const payload = {
            name,
            steps: steps.map((s) => {
                const kind = s.kind || "template";
                const reply_branches = kind !== "condition" ? mergeBranches(s, templates) : [];
                return {
                    kind,
                    template_name: s.template_name, template_language: s.template_language,
                    body_params: s.body_params, body_text: s.body_text || "",
                    cond_field: (s.cond_field || "").trim(), cond_equals: (s.cond_equals || "").trim(),
                    delay_hours: toHours(s),
                    wait_for_reply: !!s.wait_for_reply, reply_window_hours: (() => { const w = Number(s.reply_window_hours); return Number.isFinite(w) && w >= 0 ? w : 48; })(),
                    reply_mode: s.reply_mode || "any", reply_branches, on_other_reply: s.on_other_reply || "next",
                    on_reply: s.on_reply || "exit", on_no_reply: s.on_no_reply || "next",
                    on_match: s.on_match || "next", on_else: s.on_else || "next",
                };
            }),
            keyword_trigger: { enabled: triggerMode === "keyword", keyword: (kw.keyword || "").trim() },
            auto_enroll: { enabled: triggerMode === "sf", data_source: buildDS() },
            send_window: { respect_business_hours: !!sw.respect_business_hours },
            goal: { enabled: !!goal.enabled, field: (goal.field || "").trim(), equals: (goal.equals || "").trim() },
        };
        try {
            let saved;
            if (existing?.id) saved = (await api.put(`/whatsapp/journeys/${existing.id}`, payload)).data;
            else saved = (await api.post("/whatsapp/journeys", payload)).data;
            toast.success(existing?.id ? "Journey updated" : "Journey created");
            onSaved(saved?.id);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="journey-builder">
            <button onClick={onCancel} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-stone2 hover:text-ink">
                <ArrowLeft size={16} /> Back
            </button>
            <h2 className="font-heading text-2xl font-bold">{existing ? "Edit journey" : "New nurture journey"}</h2>
            <p className="mt-1 text-sm text-stone2">A sequence of approved WhatsApp templates sent on a delay. Opted-out contacts are always skipped.</p>

            <label className="mt-5 block text-xs font-semibold text-stone2">Journey name
                <input data-testid="journey-name-input" value={name} onChange={(e) => setName(e.target.value)}
                    className="mt-1 w-full max-w-md rounded-xl border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-ink" placeholder="e.g. New lead 3-day nurture" />
            </label>

            {/* Visual flow — the cadence + branching at a glance */}
            <JourneyFlowStrip steps={steps} goal={goal} />

            <div className="mt-6 space-y-4">
                {steps.map((s, i) => {
                    const tpl = (templates || []).find((t) => t.name === s.template_name);
                    return (
                        <div key={i} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`journey-step-${i}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm font-bold">
                                    <span className="grid h-6 w-6 place-items-center rounded-full bg-brand/10 text-xs text-brand">{i + 1}</span>
                                    Step {i + 1}
                                </div>
                                {steps.length > 1 && (
                                    <button data-testid={`journey-remove-step-${i}`} onClick={() => setSteps((xs) => xs.filter((_, idx) => idx !== i))}
                                        className="text-stone2 hover:text-red-600"><Trash size={16} /></button>
                                )}
                            </div>

                            <div className="mt-3 flex flex-wrap items-end gap-3">
                                <div className="inline-flex items-center gap-2 rounded-xl bg-stone-50 px-3 py-2 text-sm">
                                    <ClockCountdown size={16} className="text-brand" />
                                    {i === 0 ? "Send" : "Wait"}
                                    {i === 0 ? (
                                        <span className="font-semibold">immediately on enrol</span>
                                    ) : (
                                        <>
                                            <input data-testid={`journey-delay-value-${i}`} type="number" min="0" value={s.delay_value}
                                                onChange={(e) => setStep(i, { delay_value: e.target.value })}
                                                className="w-16 rounded-lg border border-stone-200 px-2 py-1 text-sm" />
                                            <select data-testid={`journey-delay-unit-${i}`} value={s.delay_unit} onChange={(e) => setStep(i, { delay_unit: e.target.value })}
                                                className="rounded-lg border border-stone-200 px-2 py-1 text-sm">
                                                <option value="hours">hours</option>
                                                <option value="days">days</option>
                                            </select>
                                            then send
                                        </>
                                    )}
                                </div>
                            </div>
                            {i > 0 && (
                                <p className="mt-1 pl-1 text-xs text-stone2" data-testid={`journey-delay-help-${i}`}>
                                    Delay before this step's message is sent, once a contact reaches it. Skipped when they arrive here by replying.
                                </p>
                            )}

                            <label className="mt-3 block text-xs font-semibold text-stone2">Step type
                                <select data-testid={`journey-step-kind-${i}`} value={s.kind || "template"} onChange={(e) => setStep(i, { kind: e.target.value })}
                                    className="mt-1 w-full max-w-xs rounded-xl border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-ink">
                                    {STEP_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </label>

                            {(s.kind || "template") === "template" && (<>
                                <label className="mt-3 block text-xs font-semibold text-stone2">Template
                                    <select data-testid={`journey-template-${i}`} value={s.template_name} onChange={(e) => onTemplate(i, e.target.value)}
                                        className="mt-1 w-full max-w-md rounded-xl border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-ink">
                                        <option value="">{templates === null ? "Loading templates…" : "Select an approved template"}</option>
                                        {(templates || []).map((t) => <option key={t.name} value={t.name}>{t.name} ({t.language})</option>)}
                                    </select>
                                </label>
                                {tpl?.body_text && <p className="mt-2 rounded-lg bg-stone-50 p-2 text-xs text-stone2">{tpl.body_text}</p>}
                            </>)}

                            {(s.kind || "template") === "freeform" && (
                                <label className="mt-3 block text-xs font-semibold text-stone2">Free-form message
                                    <textarea data-testid={`journey-freeform-${i}`} value={s.body_text} onChange={(e) => setStep(i, { body_text: e.target.value })} rows={3}
                                        className="mt-1 w-full max-w-md rounded-xl border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-ink"
                                        placeholder="Hi {name}, just checking in — how can we help?" />
                                    <span className="mt-1 block font-normal text-stone2">Only delivered inside the 24-hour reply window (the contact must have messaged you recently). Placeholders: {"{name}"}, {"{company}"}, {"{email}"}. If the window is closed the step is skipped.</span>
                                </label>
                            )}

                            {(s.kind || "template") === "condition" && (
                                <div className="mt-3 space-y-2">
                                    <div className="flex flex-wrap items-end gap-2">
                                        <label className="text-xs font-semibold text-stone2">Salesforce field
                                            <select data-testid={`journey-cond-field-${i}`} value={s.cond_field} onChange={(e) => setStep(i, { cond_field: e.target.value })}
                                                className="mt-1 block min-w-[200px] rounded-xl border border-stone-200 px-3 py-2 text-sm">
                                                <option value="">Select a Lead field…</option>
                                                {goalFields.map((f) => <option key={f.name} value={f.name}>{f.label} ({f.name})</option>)}
                                            </select>
                                        </label>
                                        <label className="text-xs font-semibold text-stone2">Equals
                                            <input data-testid={`journey-cond-equals-${i}`} value={s.cond_equals} onChange={(e) => setStep(i, { cond_equals: e.target.value })}
                                                placeholder="e.g. Qualified (blank = any value)"
                                                className="mt-1 block w-52 rounded-xl border border-stone-200 px-3 py-2 text-sm outline-none focus:border-ink" />
                                        </label>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-stone2">
                                        If it matches → <ActionSelect tid={`journey-cond-onmatch-${i}`} value={s.on_match} onChange={(v) => setStep(i, { on_match: v })} stepCount={steps.length} selfIdx={i} />
                                        otherwise → <ActionSelect tid={`journey-cond-onelse-${i}`} value={s.on_else} onChange={(v) => setStep(i, { on_else: v })} stepCount={steps.length} selfIdx={i} />
                                    </div>
                                </div>
                            )}

                            {(s.kind || "template") !== "condition" && (
                                <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm" data-testid={`journey-wait-${i}`}>
                                    <label className="flex items-start gap-2">
                                        <input type="checkbox" data-testid={`journey-waitreply-${i}`} checked={s.wait_for_reply}
                                            onChange={(e) => setStep(i, { wait_for_reply: e.target.checked })} className="mt-0.5" />
                                        <span><b>After sending, wait for a reply:</b> hold up to
                                            <input type="number" min="0" disabled={!s.wait_for_reply} value={s.reply_window_hours}
                                                onChange={(e) => setStep(i, { reply_window_hours: e.target.value })}
                                                data-testid={`journey-replywindow-${i}`}
                                                className="mx-1 w-16 rounded-lg border border-stone-200 px-2 py-0.5 text-sm disabled:opacity-50" />
                                            hours for a reply, then follow the "No reply" path below.</span>
                                    </label>
                                    {s.wait_for_reply && (Number(s.reply_window_hours) === 0) && (
                                        <p className="mt-1 pl-6 text-xs font-medium text-amber-700" data-testid={`journey-replywindow-warn-${i}`}>
                                            0 hours = don't wait at all — the "No reply" path fires almost instantly, so replies won't be caught. Use 24–48 for real reply branching.
                                        </p>
                                    )}
                                    {s.wait_for_reply && (() => {
                                        const tplButtons = ((templates || []).find((t) => t.name === s.template_name)?.buttons || []).filter((b) => b.type === "QUICK_REPLY");
                                        const textRules = (s.reply_branches || []).filter((b) => b.match_type !== "button");
                                        return (
                                            <div className="mt-3 space-y-3 pl-6">
                                                <div className="flex items-center gap-4 text-xs font-semibold">
                                                    <span className="text-stone2">Reply handling:</span>
                                                    <label className="flex items-center gap-1.5"><input type="radio" data-testid={`journey-replymode-any-${i}`} checked={(s.reply_mode || "any") === "any"} onChange={() => setStep(i, { reply_mode: "any" })} /> Any reply</label>
                                                    <label className="flex items-center gap-1.5"><input type="radio" data-testid={`journey-replymode-match-${i}`} checked={s.reply_mode === "match"} onChange={() => setStep(i, { reply_mode: "match" })} /> Match response</label>
                                                </div>

                                                {(s.reply_mode || "any") === "any" ? (
                                                    <div className="flex flex-wrap items-center gap-2 text-xs">
                                                        If they reply → <ActionSelect tid={`journey-onreply-${i}`} value={s.on_reply} onChange={(v) => setStep(i, { on_reply: v })} stepCount={steps.length} selfIdx={i} />
                                                        if no reply → <ActionSelect tid={`journey-onnoreply-${i}`} value={s.on_no_reply} onChange={(v) => setStep(i, { on_no_reply: v })} stepCount={steps.length} selfIdx={i} />
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2 text-xs" data-testid={`journey-branches-${i}`}>
                                                        <div className="font-semibold text-stone2">IF RESPONSE IS:</div>
                                                        {tplButtons.length === 0 && <p className="text-stone-400">This template has no quick-reply buttons — add text rules below, or use "Any reply".</p>}
                                                        {tplButtons.map((b) => (
                                                            <div key={b.text} className="flex flex-wrap items-center gap-2" data-testid={`journey-branch-btn-${i}`}>
                                                                <span className="inline-flex min-w-[150px] items-center gap-1 rounded-full bg-white px-3 py-1 font-medium text-stone-700 shadow-sm">{b.text}</span>
                                                                → <ActionSelect tid={`journey-branch-action-${i}-${b.text.replace(/\s+/g, "-").toLowerCase()}`} value={btnAction(s, b.text)} onChange={(v) => upsertBtnBranch(i, b.text, v)} stepCount={steps.length} selfIdx={i} />
                                                            </div>
                                                        ))}
                                                        {textRules.map((b, ri) => (
                                                            <div key={`t${ri}`} className="flex flex-wrap items-center gap-2" data-testid={`journey-textrule-${i}-${ri}`}>
                                                                <select value={b.match_type} onChange={(e) => setTextRule(i, ri, { match_type: e.target.value })} className="rounded-lg border border-stone-200 px-2 py-1 text-xs">
                                                                    <option value="contains">Contains</option><option value="equals">Equals</option>
                                                                </select>
                                                                <input value={b.value} onChange={(e) => setTextRule(i, ri, { value: e.target.value })} placeholder="e.g. pricing" className="w-32 rounded-lg border border-stone-200 px-2 py-1 text-xs" />
                                                                → <ActionSelect tid={`journey-textrule-action-${i}-${ri}`} value={b.action} onChange={(v) => setTextRule(i, ri, { action: v })} stepCount={steps.length} selfIdx={i} />
                                                                <button type="button" onClick={() => removeTextRule(i, ri)} className="text-stone-400 hover:text-red-500"><X size={13} /></button>
                                                            </div>
                                                        ))}
                                                        <button type="button" data-testid={`journey-add-textrule-${i}`} onClick={() => addTextRule(i)} className="font-semibold text-emerald-600 hover:underline">+ Add text rule</button>
                                                        <div className="flex flex-wrap items-center gap-2 border-t border-amber-200 pt-2">
                                                            <span className="min-w-[150px] font-medium text-stone-600">Any other reply</span> → <ActionSelect tid={`journey-onother-${i}`} value={s.on_other_reply || "next"} onChange={(v) => setStep(i, { on_other_reply: v })} stepCount={steps.length} selfIdx={i} />
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="min-w-[150px] font-medium text-stone-600">No reply after {s.reply_window_hours}h</span> → <ActionSelect tid={`journey-onnoreply-${i}`} value={s.on_no_reply} onChange={(v) => setStep(i, { on_no_reply: v })} stepCount={steps.length} selfIdx={i} />
                                                        </div>
                                                        <BranchHarness step={s} steps={steps} sidx={i} branches={mergeBranches(s, templates)} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {(s.kind || "template") === "template" && s.body_params.length > 0 && (
                                <div className="mt-3 space-y-2">
                                    <div className="text-xs font-semibold text-stone2">Template variables</div>
                                    {s.body_params.map((p, pi) => (
                                        <div key={pi} className="flex items-center gap-2">
                                            <span className="text-xs text-stone2">{`{{${pi + 1}}}`}</span>
                                            <select value={p.source} onChange={(e) => setParam(i, pi, { source: e.target.value, value: "" })}
                                                className="rounded-lg border border-stone-200 px-2 py-1.5 text-sm">
                                                <option value="static">Fixed text</option>
                                                <option value="field">Contact field</option>
                                            </select>
                                            {p.source === "field" ? (
                                                <select value={p.value} onChange={(e) => setParam(i, pi, { value: e.target.value })}
                                                    className="flex-1 rounded-lg border border-stone-200 px-2 py-1.5 text-sm">
                                                    <option value="">Select field</option>
                                                    {FIELD_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                                </select>
                                            ) : (
                                                <input value={p.value} onChange={(e) => setParam(i, pi, { value: e.target.value })}
                                                    className="flex-1 rounded-lg border border-stone-200 px-2 py-1.5 text-sm" placeholder="Fixed value" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <button data-testid="journey-add-step" onClick={() => setSteps((xs) => [...xs, emptyStep()])}
                className="mt-4 inline-flex items-center gap-2 rounded-xl border border-dashed border-stone-300 px-4 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50">
                <PlusCircle size={16} /> Add step
            </button>

            {/* Entry trigger — how contacts enter this journey */}
            <div className="mt-6 space-y-4 rounded-2xl border border-stone-200 bg-white p-5" data-testid="journey-triggers">
                <div>
                    <div className="text-sm font-bold">How do contacts enter this journey?</div>
                    <p className="mt-0.5 text-xs text-stone2">Pick one entry trigger. Opted-out contacts are always skipped.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                    {[
                        { id: "manual", icon: <ListBullets size={18} weight="duotone" />, title: "Pick a list", desc: "Enrol a Salesforce audience or CSV yourself after launch." },
                        { id: "sf", icon: <Target size={18} weight="duotone" />, title: "Salesforce auto", desc: "Auto-enrol when a lead reaches a stage or joins a list." },
                        { id: "keyword", icon: <ChatCircleDots size={18} weight="duotone" />, title: "Keyword reply", desc: "Enrol when a lead WhatsApps you a keyword." },
                    ].map((o) => (
                        <button key={o.id} type="button" data-testid={`journey-trigger-${o.id}`} onClick={() => setTriggerMode(o.id)}
                            className={`rounded-xl border p-3 text-left transition-all ${triggerMode === o.id ? "border-emerald-400 bg-emerald-50/50 ring-2 ring-emerald-200" : "border-stone-200 hover:border-stone-300"}`}>
                            <div className="flex items-center gap-2 text-sm font-bold text-ink">{o.icon} {o.title}</div>
                            <p className="mt-1 text-xs text-stone2">{o.desc}</p>
                        </button>
                    ))}
                </div>

                {triggerMode === "manual" && (
                    <p className="rounded-xl bg-stone-50 px-4 py-3 text-xs text-stone2" data-testid="journey-trigger-manual-note">
                        After you save &amp; activate, open the journey and click <b>Enrol contacts</b> to add a Salesforce audience or CSV.
                    </p>
                )}

                {triggerMode === "keyword" && (
                    <div data-testid="journey-trigger-keyword-cfg">
                        <label className="block text-xs font-semibold text-stone2">Keyword</label>
                        <input data-testid="journey-keyword-input" value={kw.keyword} onChange={(e) => setKw({ ...kw, keyword: e.target.value })}
                            placeholder="e.g. INFO" className="mt-1 w-full max-w-xs rounded-xl border border-stone-200 px-3 py-2 text-sm outline-none focus:border-ink" />
                        <p className="mt-1 text-xs text-stone2">A contact who WhatsApps you this word (case-insensitive) is enrolled instantly.</p>
                    </div>
                )}

                {triggerMode === "sf" && (
                    <div className="space-y-4" data-testid="journey-trigger-sf-cfg">
                        <div className="inline-flex rounded-full bg-stone-100 p-1 text-xs font-semibold">
                            <button type="button" data-testid="journey-automode-stage" onClick={() => setAutoMode("stage")}
                                className={`rounded-full px-3 py-1.5 transition-colors ${autoMode === "stage" ? "bg-white text-ink shadow-sm" : "text-stone2"}`}>When a lead reaches a stage</button>
                            <button type="button" data-testid="journey-automode-list" onClick={() => setAutoMode("list")}
                                className={`rounded-full px-3 py-1.5 transition-colors ${autoMode === "list" ? "bg-white text-ink shadow-sm" : "text-stone2"}`}>From a list / report / campaign</button>
                        </div>

                        {autoMode === "stage" && (
                            <>
                                <div className="flex flex-wrap items-end gap-2" data-testid="journey-stage-builder">
                                    <span className="pb-2 text-sm text-stone-600">When a</span>
                                    <select data-testid="journey-stage-object" value={stage.object} onChange={(e) => setStage({ ...stage, object: e.target.value, value: "" })}
                                        className="rounded-xl border border-stone-200 px-3 py-2 text-sm">
                                        <option value="Lead">Lead</option><option value="Contact">Contact</option>
                                    </select>
                                    <span className="pb-2 text-sm text-stone-600">'s</span>
                                    <select data-testid="journey-stage-field" value={stage.field} onChange={(e) => setStage({ ...stage, field: e.target.value, value: "" })}
                                        className="rounded-xl border border-stone-200 px-3 py-2 text-sm">
                                        {STAGE_FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                    <span className="pb-2 text-sm text-stone-600">becomes</span>
                                    {(filterOpts[stage.field] || []).length > 0 ? (
                                        <select data-testid="journey-stage-value" value={stage.value} onChange={(e) => setStage({ ...stage, value: e.target.value })}
                                            className="min-w-[160px] rounded-xl border border-stone-200 px-3 py-2 text-sm">
                                            <option value="">Select value</option>
                                            {(filterOpts[stage.field] || []).map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                    ) : (
                                        <input data-testid="journey-stage-value" value={stage.value} onChange={(e) => setStage({ ...stage, value: e.target.value })}
                                            placeholder="e.g. Qualified" className="min-w-[160px] rounded-xl border border-stone-200 px-3 py-2 text-sm" />
                                    )}
                                    <span className="pb-2 text-sm font-semibold text-emerald-600">→ enrol</span>
                                </div>
                                <p className="text-xs text-stone2">Contacts join within ~1 min of their Salesforce {stage.object} matching this. (Lead <b>Status</b> is Salesforce's lead "stage".)</p>
                            </>
                        )}

                        {autoMode === "list" && (
                            <div className="flex flex-wrap items-center gap-2" data-testid="journey-list-builder">
                                <select data-testid="journey-auto-kind" value={auto.kind} onChange={(e) => setAuto({ ...auto, kind: e.target.value })}
                                    className="rounded-xl border border-stone-200 px-3 py-2 text-sm">
                                    <option value="sf_leads">All Leads</option>
                                    <option value="sf_contacts">All Contacts</option>
                                    <option value="sf_listview">List View</option>
                                    <option value="sf_report">Report</option>
                                    <option value="sf_campaign">Campaign</option>
                                </select>
                                {auto.kind === "sf_listview" && (
                                    <>
                                        <select value={auto.list_view_object} onChange={(e) => setAuto({ ...auto, list_view_object: e.target.value, list_view_id: "" })}
                                            className="rounded-xl border border-stone-200 px-3 py-2 text-sm">
                                            <option value="Lead">Lead</option><option value="Contact">Contact</option>
                                        </select>
                                        <select data-testid="journey-auto-listview" value={auto.list_view_id} onChange={(e) => setAuto({ ...auto, list_view_id: e.target.value })}
                                            className="min-w-[200px] rounded-xl border border-stone-200 px-3 py-2 text-sm">
                                            <option value="">Select a list view</option>
                                            {sfOpts.listviews.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                                        </select>
                                    </>
                                )}
                                {auto.kind === "sf_report" && (
                                    <select data-testid="journey-auto-report" value={auto.report_id} onChange={(e) => setAuto({ ...auto, report_id: e.target.value })}
                                        className="min-w-[220px] rounded-xl border border-stone-200 px-3 py-2 text-sm">
                                        <option value="">Select a report</option>
                                        {sfOpts.reports.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                                    </select>
                                )}
                                {auto.kind === "sf_campaign" && (
                                    <select data-testid="journey-auto-campaign" value={auto.sf_campaign_id} onChange={(e) => setAuto({ ...auto, sf_campaign_id: e.target.value })}
                                        className="min-w-[220px] rounded-xl border border-stone-200 px-3 py-2 text-sm">
                                        <option value="">Select a campaign</option>
                                        {sfOpts.campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                )}
                            </div>
                        )}

                        {existing?.id && (
                            <div className="flex items-center gap-3 border-t border-stone-100 pt-3">
                                <button type="button" data-testid="journey-sync-now" onClick={syncNow} disabled={syncing}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50">
                                    <CircleNotch size={13} className={syncing ? "animate-spin" : ""} /> Sync now
                                </button>
                                <span className="text-xs text-stone2" data-testid="journey-last-synced">{lastSynced ? `Last synced ${new Date(lastSynced).toLocaleString()}` : "Not synced yet"}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Sending window (quiet hours) */}
            <div className="mt-4 space-y-3 rounded-2xl border border-stone-200 bg-white p-5" data-testid="journey-send-window">
                <div className="text-sm font-bold">Sending window (optional)</div>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid="journey-business-hours-enabled" checked={!!sw.respect_business_hours}
                        onChange={(e) => setSw({ respect_business_hours: e.target.checked })} />
                    <span>Only send during business hours</span>
                </label>
                <p className="text-xs text-stone2">Uses your workspace's Business Hours &amp; timezone (Settings → General). Steps that come due outside these hours are held and sent at the next opening time — so customers are never messaged overnight, protecting your number's quality rating.</p>
            </div>

            {/* Goal / exit condition */}
            <div className="mt-4 space-y-3 rounded-2xl border border-stone-200 bg-white p-5" data-testid="journey-goal">
                <div className="text-sm font-bold">Goal &amp; exit (optional)</div>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid="journey-goal-enabled" checked={!!goal.enabled}
                        onChange={(e) => setGoal({ ...goal, enabled: e.target.checked })} />
                    <span>Exit a contact (as converted) when their Salesforce record hits a target</span>
                </label>
                {goal.enabled && (
                    <div className="flex flex-wrap items-end gap-2">
                        <label className="text-xs text-stone2">Salesforce field
                            <select data-testid="journey-goal-field" value={goal.field} onChange={(e) => setGoal({ ...goal, field: e.target.value })}
                                className="mt-1 block min-w-[220px] rounded-xl border border-stone-200 px-3 py-2 text-sm">
                                <option value="">Select a Lead field…</option>
                                {goalFields.map((f) => <option key={f.name} value={f.name}>{f.label} ({f.name})</option>)}
                            </select>
                        </label>
                        <label className="text-xs text-stone2">Equals
                            <input data-testid="journey-goal-equals" value={goal.equals} onChange={(e) => setGoal({ ...goal, equals: e.target.value })}
                                placeholder="e.g. Qualified (blank = any value)"
                                className="mt-1 block w-56 rounded-xl border border-stone-200 px-3 py-2 text-sm outline-none focus:border-ink" />
                        </label>
                    </div>
                )}
                {goal.enabled && <p className="text-xs text-stone2">Checked before every step. When the enrolled Lead/Contact's field matches, the contact stops receiving further messages and is counted as a goal conversion.</p>}
            </div>

            <div className="mt-6 flex gap-2">
                <button onClick={onCancel} className="rounded-full border border-stone-300 px-5 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-100">Cancel</button>
                <button data-testid="journey-save-btn" onClick={save} disabled={saving}
                    className="rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
                    {saving ? "Saving…" : existing ? "Save changes" : "Create journey"}
                </button>
            </div>
        </div>
    );
}

/* ---------------- Enroll modal ---------------- */
function EnrollModal({ journey, onClose, onEnrolled }) {
    const [sel, setSel] = useState({ dataSource: { kind: "csv" }, records: [], ready: false });
    const [busy, setBusy] = useState(false);

    const enroll = async () => {
        if (!sel.ready) return toast.error("Choose an audience (upload a CSV, paste numbers, or preview a Salesforce source).");
        setBusy(true);
        try {
            const { data } = await api.post(`/whatsapp/journeys/${journey.id}/enroll`,
                { data_source: sel.dataSource, records: sel.records });
            toast.success(`${data.activated ? "Journey activated · " : ""}Enrolled ${data.enrolled} · skipped ${data.skipped}`);
            onEnrolled();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="journey-enroll-modal">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                    <h3 className="font-heading text-xl font-bold">Enrol contacts</h3>
                    <button onClick={onClose} className="text-stone2 hover:text-ink"><X size={20} /></button>
                </div>
                <p className="mt-1 text-sm text-stone2">Add contacts to “{journey.name}” from a CSV or a Salesforce audience. Opted-out and already-enrolled contacts are skipped automatically.</p>

                <div className="mt-4">
                    <AudiencePicker tidPrefix="journey" onChange={setSel} />
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-600 hover:bg-stone-100">Cancel</button>
                    <button data-testid="journey-enroll-btn" onClick={enroll} disabled={busy || !sel.ready}
                        className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
                        {busy ? <CircleNotch size={15} className="animate-spin" /> : <Users size={15} />} Enrol
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ---------------- Detail ---------------- */
function JourneyDetail({ id, onBack, onEdit }) {
    const [j, setJ] = useState(null);
    const [enroll, setEnroll] = useState(false);
    const [analytics, setAnalytics] = useState(null);
    const [running, setRunning] = useState(false);
    const [runResult, setRunResult] = useState(null);

    const load = () => {
        if (!id) return;
        api.get(`/whatsapp/journeys/${id}`).then((r) => setJ(r.data)).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
        api.get(`/whatsapp/journeys/${id}/analytics`).then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [id]);

    const act = async (action) => {
        try {
            await api.post(`/whatsapp/journeys/${id}/${action}`);
            toast.success(action === "activate" ? "Journey activated" : "Journey paused");
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    };

    const runNow = async () => {
        setRunning(true);
        setRunResult(null);
        try {
            const { data } = await api.post(`/whatsapp/journeys/${id}/run-now`);
            setRunResult(data);
            const errs = (data.errors || []).length;
            if (data.processed === 0 && !errs) toast.info("Nothing due to send right now.");
            else if (errs) toast.warning(`Processed ${data.processed} · ${errs} error(s) — see details below`);
            else toast.success(`Processed ${data.processed} due run(s) — messages sent`);
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
        } finally { setRunning(false); }
    };

    if (!j) return <div className="py-16 text-center text-sm text-stone2">Loading…</div>;
    const c = j.counts || {};
    return (
        <div data-testid="journey-detail">
            <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-stone2 hover:text-ink"><ArrowLeft size={16} /> All journeys</button>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold">{j.name}</h2>
                    <span className={`mt-1 inline-block rounded-full px-3 py-0.5 text-xs font-bold capitalize ${STATUS_BADGE[j.status]}`}>{j.status}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button data-testid="journey-enroll-open" onClick={() => setEnroll(true)} className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white"><Users size={15} /> Enrol contacts</button>
                    {j.status === "active" && (
                        <button data-testid="journey-run-now" onClick={runNow} disabled={running}
                            className="inline-flex items-center gap-2 rounded-full border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
                            {running ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneTilt size={15} weight="fill" />} Send due now
                        </button>
                    )}
                    {j.status === "active" ? (
                        <button data-testid="journey-pause" onClick={() => act("pause")} className="inline-flex items-center gap-2 rounded-full border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50"><Pause size={15} /> Pause</button>
                    ) : (
                        <button data-testid="journey-activate" onClick={() => act("activate")} className="inline-flex items-center gap-2 rounded-full border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"><Play size={15} /> Activate</button>
                    )}
                    {j.status !== "active" && <button data-testid="journey-edit" onClick={() => onEdit(j)} className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-600 hover:bg-stone-100">Edit</button>}
                </div>
            </div>

            {j.status !== "active" && (
                <div data-testid="journey-inactive-warning" className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4">
                    <div className="flex items-start gap-3 text-sm text-amber-900">
                        <Pause size={18} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
                        <div>
                            <div className="font-semibold">This journey is {j.status === "paused" ? "paused" : "a draft"} — it will not send any messages.</div>
                            <div className="mt-0.5 text-amber-800">{(c.total || 0) > 0 ? `${c.total} contact(s) are enrolled but will NOT receive messages until you activate it. ` : ""}Click Activate to start sending.</div>
                        </div>
                    </div>
                    <button data-testid="journey-activate-banner" onClick={() => act("activate")} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-700"><Play size={15} weight="fill" /> Activate now</button>
                </div>
            )}

            {runResult && (
                <div data-testid="journey-run-result" className={`mt-4 rounded-2xl border px-5 py-4 ${(runResult.errors || []).length ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}>
                    <div className="flex items-center gap-2 text-sm font-semibold">
                        <PaperPlaneTilt size={16} weight="fill" className={(runResult.errors || []).length ? "text-red-600" : "text-emerald-600"} />
                        Processed {runResult.processed} due run(s){(runResult.errors || []).length ? ` · ${runResult.errors.length} error(s)` : " — no errors"}
                    </div>
                    {(runResult.errors || []).length > 0 ? (
                        <ul className="mt-2 space-y-1 text-xs text-red-800" data-testid="journey-run-errors">
                            {runResult.errors.map((er, k) => (
                                <li key={k} className="flex flex-wrap items-center gap-2">
                                    <span className="font-mono font-semibold">{er.contact}</span>
                                    <span className="rounded-full bg-white px-2 py-0.5 font-semibold capitalize text-red-600">{er.status}</span>
                                    <span>{er.error}</span>
                                </li>
                            ))}
                        </ul>
                    ) : runResult.processed === 0 ? (
                        <p className="mt-1 text-xs text-emerald-800">Nothing was due to send right now. Newly-enrolled contacts and steps waiting on a delay/reply are not "due" until their scheduled time.</p>
                    ) : (
                        <p className="mt-1 text-xs text-emerald-800">All due messages were sent successfully.</p>
                    )}
                </div>
            )}

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-6">
                {[["Enrolled", c.total || 0], ["Active", c.active || 0], ["Completed", c.completed || 0], ["Converted", c.converted || 0], ["Stopped", c.stopped || 0], ["Messages sent", c.messages_sent || 0]].map(([l, v]) => (
                    <div key={l} className="rounded-xl border border-stone-200 bg-white p-4"><div className="text-2xl font-bold">{v}</div><div className="text-xs text-stone2">{l}</div></div>
                ))}
            </div>

            <JourneyFlowStrip steps={j.steps} goal={j.goal} />

            {(j.status !== "draft") && (
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {j.keyword_trigger?.enabled && <span className="rounded-full bg-sky-50 px-3 py-1 font-semibold text-sky-700">Keyword enrol: “{j.keyword_trigger.keyword}”</span>}
                    {j.auto_enroll?.enabled && <span className="rounded-full bg-violet-50 px-3 py-1 font-semibold text-violet-700">Salesforce auto-enrol ON</span>}
                    {j.send_window?.respect_business_hours && <span className="rounded-full bg-teal-50 px-3 py-1 font-semibold text-teal-700">Business-hours only</span>}
                    {j.goal?.enabled && <span data-testid="journey-goal-badge" className="rounded-full bg-fuchsia-50 px-3 py-1 font-semibold text-fuchsia-700">Goal: {j.goal.field}{j.goal.equals ? ` = ${j.goal.equals}` : ""}{c.goal_converted ? ` · ${c.goal_converted} met` : ""}</span>}
                </div>
            )}

            <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold"><Target size={16} weight="duotone" className="text-indigo-500" /> Step performance</div>
                <ol className="space-y-2">
                    {(j.steps || []).map((s, i) => {
                        const a = (analytics?.steps || [])[i] || {};
                        const kind = s.kind || "template";
                        const label = kind === "freeform" ? (s.body_text || "Free-form message")
                            : kind === "condition" ? `If ${s.cond_field}${s.cond_equals ? ` = ${s.cond_equals}` : ""}`
                            : s.template_name;
                        const branches = a.branches || {};
                        return (
                            <li key={i} className="rounded-xl border border-stone-100 bg-stone-50/50 p-3 text-sm" data-testid={`step-perf-${i}`}>
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="grid h-6 w-6 place-items-center rounded-full bg-brand/10 text-xs text-brand">{i + 1}</span>
                                    <PaperPlaneTilt size={15} className="text-stone2" />
                                    {kind !== "template" && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${kind === "freeform" ? "bg-cyan-50 text-cyan-700" : "bg-fuchsia-50 text-fuchsia-700"}`}>{kind === "freeform" ? "Free-form" : "Condition"}</span>}
                                    <span className="max-w-[260px] truncate font-semibold">{label}</span>
                                    <span className="text-stone2">{i === 0 ? "· immediately" : `· after ${s.delay_hours >= 24 && s.delay_hours % 24 === 0 ? `${s.delay_hours / 24}d` : `${s.delay_hours}h`}`}</span>
                                    {s.wait_for_reply && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">↩ wait {s.reply_window_hours}h · {(s.reply_mode || "any") === "match" ? "Match" : "Any"}</span>}
                                    <span className="ml-auto flex flex-wrap gap-1.5 text-xs" data-testid={`step-stats-${i}`}>
                                        <span className="rounded-full bg-sky-50 px-2 py-0.5 font-semibold text-sky-700">{a.sent || 0} sent</span>
                                        {(a.failed || 0) > 0 && <span data-testid={`step-failed-${i}`} className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700">{a.failed} failed</span>}
                                        {s.wait_for_reply && <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">{a.replied || 0} replied</span>}
                                        {s.wait_for_reply && <span className="rounded-full bg-stone-100 px-2 py-0.5 font-semibold text-stone-600">{a.no_reply || 0} no reply</span>}
                                    </span>
                                </div>
                                {(a.failed || 0) > 0 && a.last_error && (
                                    <div className="mt-2 flex items-start gap-1.5 pl-9 text-xs text-red-700" data-testid={`step-failerror-${i}`}>
                                        <span className="font-semibold">Delivery failed:</span> {a.last_error}
                                    </div>
                                )}
                                {Object.keys(branches).length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5 pl-9" data-testid={`step-branches-${i}`}>
                                        {Object.entries(branches).map(([b, n]) => (
                                            <span key={b} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-0.5 text-xs text-stone-600 shadow-sm">
                                                {b === "any_other" ? "Any other reply" : b === "no_reply" ? "No reply" : b === "any_reply" ? "Any reply" : b}
                                                <b className="text-indigo-600">{n}</b>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ol>
            </div>

            <div className="mt-6 overflow-hidden rounded-2xl border border-stone-200 bg-white">
                <table className="w-full text-left text-sm">
                    <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone2">
                        <tr><th className="px-5 py-3">Contact</th><th className="px-5 py-3">Step</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Next due</th></tr>
                    </thead>
                    <tbody>
                        {(j.runs || []).length === 0 ? (
                            <tr><td colSpan={4} className="px-5 py-10 text-center text-stone2" data-testid="journey-runs-empty">No one enrolled yet.</td></tr>
                        ) : j.runs.map((r) => (
                            <tr key={r.id} className="border-t border-stone-100">
                                <td className="px-5 py-3">{r.name || r.contact}</td>
                                <td className="px-5 py-3">{Math.min((r.step_index || 0) + 1, (j.steps || []).length)}/{(j.steps || []).length}</td>
                                <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${RUN_BADGE[r.status] || ""}`}>{r.status}</span>{r.last_error ? <span className="ml-2 text-xs text-red-500">{r.last_error}</span> : null}</td>
                                <td className="px-5 py-3 text-stone-500">{r.next_due_at ? new Date(r.next_due_at).toLocaleString() : "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {enroll && <EnrollModal journey={j} onClose={() => setEnroll(false)} onEnrolled={() => { setEnroll(false); load(); }} />}
        </div>
    );
}

/* ---------------- Root ---------------- */
export default function WhatsAppJourneys() {
    const [view, setView] = useState("list");   // list | create | edit | detail
    const [journeys, setJourneys] = useState(null);
    const [activeId, setActiveId] = useState(null);
    const [editing, setEditing] = useState(null);
    const [prefill, setPrefill] = useState(null);
    const [blueprints, setBlueprints] = useState([]);

    const load = () => api.get("/whatsapp/journeys").then((r) => setJourneys(r.data.journeys || [])).catch(() => setJourneys([]));
    useEffect(() => {
        load();
        api.get("/whatsapp/journeys/blueprints").then((r) => setBlueprints(r.data.blueprints || [])).catch(() => setBlueprints([]));
    }, []);

    const del = async (j) => {
        if (!window.confirm(`Delete journey “${j.name}” and all its enrolments?`)) return;
        try { await api.delete(`/whatsapp/journeys/${j.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    };

    const applyBlueprint = (b) => { setPrefill(b.steps || []); setView("create"); };
    const cloneJourney = (j) => {
        // Open the builder in CREATE mode prefilled with everything from the source journey.
        setEditing({ ...j, id: undefined, name: `${j.name} (Copy)`, status: "draft" });
        setView("edit");
    };

    if (view === "create") return <JourneyBuilder prefillSteps={prefill} onCancel={() => { setPrefill(null); setView("list"); }} onSaved={() => { setPrefill(null); setView("list"); load(); }} />;
    if (view === "edit") return <JourneyBuilder existing={editing} onCancel={() => setView("detail")} onSaved={(sid) => { if (sid) setActiveId(sid); setView("detail"); load(); }} />;
    if (view === "detail") return <JourneyDetail id={activeId} onBack={() => { setView("list"); load(); }} onEdit={(j) => { setEditing(j); setView("edit"); }} />;

    return (
        <div data-testid="journeys-tab">
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2 text-brand"><FlowArrow size={20} weight="duotone" /><span className="text-xs font-bold uppercase tracking-widest">Nurture</span></div>
                    <h2 className="mt-1 font-heading text-2xl font-bold">Journeys</h2>
                    <p className="mt-1 text-sm text-stone2">Automated retargeting drips — send a sequence of approved templates over time.</p>
                </div>
                <button data-testid="new-journey-btn" onClick={() => { setPrefill(null); setView("create"); }} className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-hover"><PlusCircle size={16} /> New journey</button>
            </div>

            {blueprints.length > 0 && (
                <div className="mt-6" data-testid="journey-blueprints">
                    <div className="text-xs font-bold uppercase tracking-widest text-stone2">Start from a template</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {blueprints.map((b) => (
                            <div key={b.id} data-testid={`journey-blueprint-${b.id}`} className="flex flex-col rounded-2xl border border-stone-200 bg-white p-4">
                                <div className="flex items-center gap-2 text-sm font-bold"><Sparkle size={16} weight="duotone" className="text-brand" /> {b.name}</div>
                                <p className="mt-1 flex-1 text-xs text-stone2">{b.description}</p>
                                <div className="mt-2 text-[11px] text-stone2">{(b.steps || []).length} step(s)</div>
                                <button data-testid={`journey-blueprint-use-${b.id}`} onClick={() => applyBlueprint(b)}
                                    className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-full border border-brand px-3 py-1.5 text-xs font-semibold text-brand hover:bg-brand hover:text-white">
                                    <PlusCircle size={14} /> Use this
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-6 space-y-3">
                {journeys === null ? <div className="py-16 text-center text-sm text-stone2">Loading…</div>
                    : journeys.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-stone-300 py-16 text-center" data-testid="journeys-empty">
                            <p className="text-sm text-stone2">No journeys yet. Create a nurture drip to re-engage leads automatically.</p>
                        </div>
                    ) : journeys.map((j) => (
                        <div key={j.id} data-testid={`journey-row-${j.id}`} className="flex items-center justify-between gap-4 rounded-2xl border border-stone-200 bg-white px-5 py-4">
                            <button onClick={() => { setActiveId(j.id); setView("detail"); }} className="min-w-0 flex-1 text-left">
                                <div className="flex items-center gap-2">
                                    <span className="truncate text-lg font-semibold">{j.name}</span>
                                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${STATUS_BADGE[j.status]}`}>{j.status}</span>
                                </div>
                                <div className="mt-0.5 text-xs text-stone2">{(j.steps || []).length} step(s) · {j.counts?.total || 0} enrolled · {j.counts?.messages_sent || 0} sent</div>
                            </button>
                            <div className="flex items-center gap-1">
                                {j.status !== "active" && (
                                    <button data-testid={`journey-edit-${j.id}`} onClick={() => { setActiveId(j.id); setEditing(j); setView("edit"); }}
                                        title="Edit journey" className="rounded-lg p-2 text-stone2 hover:bg-stone-100 hover:text-ink"><PencilSimple size={16} /></button>
                                )}
                                <button data-testid={`journey-clone-${j.id}`} onClick={() => cloneJourney(j)}
                                    title="Duplicate journey" className="rounded-lg p-2 text-stone2 hover:bg-stone-100 hover:text-ink"><CopySimple size={16} /></button>
                                <button data-testid={`journey-delete-${j.id}`} onClick={() => del(j)} title="Delete journey" className="rounded-lg p-2 text-stone2 hover:bg-red-50 hover:text-red-600"><Trash size={16} /></button>
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    );
}
